import { z } from 'zod'
import {
  clearableOptionalEmail,
  clearableOptionalText,
  clearableOptionalUrl,
  optionalEmail,
  optionalText,
  optionalUrl,
} from './fields'

export const SUPPLIER_CATEGORIES = [
  'venue',
  'officiant',
  'registry-office',
  'church',
  'catering',
  'cake-sweets',
  'bar',
  'photographer',
  'videographer',
  'photo-video-team',
  'dj-moderator',
  'band',
  'photo-booth',
  'entertainment',
  'makeup',
  'hair',
  'florist',
  'decor',
  'rentals',
  'stationery',
  'bride-attire',
  'groom-attire',
  'rings',
  'accessories',
  'coordination',
  'transport',
  'childcare',
  'other',
] as const

export const WEDDING_SUPPLIER_STATUSES = [
  'contacted',
  'shortlisted',
  'awaiting-offer',
  'selected',
  'contract-pending',
  'booked',
  'cancelled',
] as const

export const SUPPLIER_SERVICE_AREAS = [
  'all-czechia',
  'prague',
  'central-bohemian',
  'south-bohemian',
  'plzen',
  'karlovy-vary',
  'usti-nad-labem',
  'liberec',
  'hradec-kralove',
  'pardubice',
  'vysocina',
  'south-moravian',
  'olomouc',
  'zlin',
  'moravian-silesian',
] as const

export const supplierCategorySchema = z.enum(SUPPLIER_CATEGORIES)
export const weddingSupplierStatusSchema = z.enum(WEDDING_SUPPLIER_STATUSES)
export const supplierServiceAreaSchema = z.enum(SUPPLIER_SERVICE_AREAS)

const supplierServiceAreasSchema = z
  .array(supplierServiceAreaSchema)
  .max(SUPPLIER_SERVICE_AREAS.length)
  .refine((areas) => new Set(areas).size === areas.length, {
    params: { reason: 'duplicate-service-area' },
  })
  .refine((areas) => !areas.includes('all-czechia') || areas.length === 1, {
    params: { reason: 'all-czechia-exclusive' },
  })

const supplierNameSchema = z.string().trim().min(1).max(160)

const supplierCategoriesSchema = z
  .array(supplierCategorySchema)
  .min(1)
  .max(SUPPLIER_CATEGORIES.length)
  .refine((categories) => new Set(categories).size === categories.length, {
    params: { reason: 'duplicate-category' },
  })

/** The one flat profile of public catalog suppliers and of a wedding's custom ones. */
export const supplierProfileSchema = z.object({
  name: supplierNameSchema,
  categories: supplierCategoriesSchema,
  description: optionalText(3000),
  email: optionalEmail,
  phone: optionalText(60),
  website: optionalUrl,
  instagram: optionalText(120),
  facebook: optionalText(120),
  tiktok: optionalText(120),
  youtube: optionalText(120),
  address: optionalText(300),
  serviceArea: supplierServiceAreasSchema.optional(),
  companyId: optionalText(40),
  taxId: optionalText(40),
})

export const updateSupplierProfileSchema = z.object({
  name: supplierNameSchema.optional(),
  categories: supplierCategoriesSchema.optional(),
  description: clearableOptionalText(3000),
  email: clearableOptionalEmail,
  phone: clearableOptionalText(60),
  website: clearableOptionalUrl,
  instagram: clearableOptionalText(120),
  facebook: clearableOptionalText(120),
  tiktok: clearableOptionalText(120),
  youtube: clearableOptionalText(120),
  address: clearableOptionalText(300),
  serviceArea: supplierServiceAreasSchema.optional(),
  companyId: clearableOptionalText(40),
  taxId: clearableOptionalText(40),
})

const weddingSupplierRolesSchema = z.array(supplierCategorySchema).min(1)

/** Links a visible catalog supplier; without roles the server uses its first category. */
export const createWeddingSupplierSchema = z.object({
  catalogSupplierId: z.uuid(),
  roles: weddingSupplierRolesSchema.optional(),
  status: weddingSupplierStatusSchema.default('contacted'),
})

/** Creates the wedding's private catalog row and links it in one operation. */
export const createCustomWeddingSupplierSchema = z.object({
  name: supplierNameSchema,
  category: supplierCategorySchema.exclude(['venue']),
  email: optionalEmail,
  phone: optionalText(60),
  notes: optionalText(2000),
  status: weddingSupplierStatusSchema.optional(),
})

export const updateWeddingSupplierSchema = z.object({
  roles: weddingSupplierRolesSchema.optional(),
  status: weddingSupplierStatusSchema.optional(),
})

/** Notes live on the wedding's link; the profile is editable only on a custom supplier. */
export const updateWeddingSupplierDetailsSchema = z.object({
  notes: clearableOptionalText(2000),
  profile: updateSupplierProfileSchema.optional(),
})

export const createWeddingSupplierContactSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    role: optionalText(120),
    phone: optionalText(60),
    email: optionalEmail,
  })
  .refine((value) => value.phone || value.email, {
    params: { reason: 'contact-method-required' },
  })

export const updateWeddingSupplierContactSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: clearableOptionalText(120),
  phone: clearableOptionalText(60),
  email: clearableOptionalEmail,
})

export type SupplierCategory = (typeof SUPPLIER_CATEGORIES)[number]
export type SupplierServiceArea = (typeof SUPPLIER_SERVICE_AREAS)[number]
export type WeddingSupplierStatus = (typeof WEDDING_SUPPLIER_STATUSES)[number]
export type SupplierProfileInput = z.infer<typeof supplierProfileSchema>
export type UpdateSupplierProfileInput = z.infer<typeof updateSupplierProfileSchema>
export type CreateWeddingSupplierInput = z.infer<typeof createWeddingSupplierSchema>
export type CreateCustomWeddingSupplierInput = z.infer<typeof createCustomWeddingSupplierSchema>
export type UpdateWeddingSupplierInput = z.infer<typeof updateWeddingSupplierSchema>
export type UpdateWeddingSupplierDetailsInput = z.infer<typeof updateWeddingSupplierDetailsSchema>
export type CreateWeddingSupplierContactInput = z.infer<typeof createWeddingSupplierContactSchema>
export type UpdateWeddingSupplierContactInput = z.infer<typeof updateWeddingSupplierContactSchema>
